import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportReferrals } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function ReferralsReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-referrals', qBuilt],
    queryFn: () => fetchReportReferrals(qBuilt),
  });

  const d = q.data;

  const chartRows = useMemo(
    () => (d?.chart ?? []).map((r) => ({ ...r, dLabel: r.date.length > 10 ? r.date.slice(0, 10) : r.date })),
    [d],
  );

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.topReferrers.map((r) => ({ name: r.name, referralCount: r.referralCount, revenueGenerated: r.revenueGenerated })),
      'referrals.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading referrals…</div>;
  if (q.isError || !d?.summary || !d.topReferrers) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load referrals report.</div>;
  }

  const s = d.summary;

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Total referrals', v: String(s.totalReferrals) },
          { label: 'Converted', v: String(s.convertedReferrals) },
          { label: 'Conversion rate', v: `${s.conversionRate.toFixed(1)}%` },
          { label: 'Revenue from referrals', v: formatCurrency(s.totalRevenueFromReferrals) },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Referrals over time</h3>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dLabel" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" name="Referrals" stroke="#E8793A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Top referrer</th>
              <th className="px-4 py-3">Referrals</th>
              <th className="px-4 py-3">Revenue generated</th>
            </tr>
          </thead>
          <tbody>
            {d.topReferrers.map((r) => (
              <tr key={r.customerId} className="border-b border-slate-100">
                <td className="px-4 py-2 font-medium">
                  <Link to={`/customers/${r.customerId}`} className="text-primary hover:underline">
                    {r.name}
                  </Link>
                </td>
                <td className="px-4 py-2">{r.referralCount}</td>
                <td className="px-4 py-2">{formatCurrency(r.revenueGenerated)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.topReferrers.length ? <p className="p-6 text-center text-slate-500">No referral activity in this period.</p> : null}
      </div>
    </div>
  );
}
